import {fetchMessarioAiGenerateBundle, MessarioAiFetchFailure} from '@lib/messarioAi/api';
import {
  MessarioAiBundleEnabled,
  MessarioAiGenerateBundleEnabled
} from '@lib/messarioAi/types';

export const MESSARIO_AI_GENERATE_BUNDLE_KEY_PREFIX = 'messario_ai_generate_bundle:';

function storageKey(externalUserId: string) {
  return `${MESSARIO_AI_GENERATE_BUNDLE_KEY_PREFIX}${externalUserId}`;
}

function isGenerateBundleEnabled(value: unknown): value is MessarioAiGenerateBundleEnabled {
  if(!value || typeof value !== 'object') {
    return false;
  }

  const bundle = value as MessarioAiGenerateBundleEnabled;
  return bundle.enabled === true &&
    typeof bundle.model === 'string' &&
    typeof bundle.reasoning_effort === 'string' &&
    typeof bundle.history_limit === 'number' &&
    typeof bundle.max_output_tokens === 'number' &&
    typeof bundle.timezone === 'string' &&
    typeof bundle.channel === 'string' &&
    typeof bundle.json_schema_name === 'string' &&
    !!bundle.json_schema_name &&
    Array.isArray(bundle.definitions) &&
    typeof bundle.prompt_revision === 'number' &&
    typeof bundle.variable_definitions_revision === 'number' &&
    !!bundle.features &&
    typeof bundle.features.one_shot_instruction === 'boolean' &&
    typeof bundle.features.draft_only === 'boolean' &&
    (bundle.folder_id === null || typeof bundle.folder_id === 'number') &&
    (bundle.compiled_at === null || typeof bundle.compiled_at === 'string');
}

function stripSecrets<T extends Record<string, unknown>>(bundle: T): T {
  const copy = {...bundle};
  delete copy.api_key;
  delete copy.compiled_system_prompt;
  delete copy.json_schema;
  return copy;
}

export function generateBundleMatchesMetadata(
  cached: MessarioAiGenerateBundleEnabled,
  metadata: MessarioAiBundleEnabled
): boolean {
  if(cached.prompt_revision !== metadata.prompt_revision) {
    return false;
  }

  if(typeof metadata.variable_definitions_revision === 'number' &&
    cached.variable_definitions_revision !== metadata.variable_definitions_revision) {
    return false;
  }

  return true;
}

export function getMessarioAiGenerateBundleCache(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiGenerateBundleEnabled | undefined {
  const value = storage.getItem(storageKey(externalUserId));
  if(!value) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if(!isGenerateBundleEnabled(parsed)) {
      storage.removeItem(storageKey(externalUserId));
      return undefined;
    }

    const cleaned = stripSecrets(parsed as MessarioAiGenerateBundleEnabled & Record<string, unknown>);
    const encoded = JSON.stringify(cleaned);
    if(encoded !== value) {
      storage.setItem(storageKey(externalUserId), encoded);
    }

    return cleaned;
  } catch{
    storage.removeItem(storageKey(externalUserId));
    return undefined;
  }
}

export function saveMessarioAiGenerateBundleCache(
  externalUserId: string,
  bundle: MessarioAiGenerateBundleEnabled,
  storage: Storage = window.sessionStorage
) {
  if(!externalUserId || !isGenerateBundleEnabled(bundle)) {
    return;
  }

  storage.setItem(
    storageKey(externalUserId),
    JSON.stringify(stripSecrets(bundle as unknown as Record<string, unknown>))
  );
}

export function clearMessarioAiGenerateBundleCache(
  externalUserId: string,
  storage: Storage = window.sessionStorage
) {
  storage.removeItem(storageKey(externalUserId));
}

export async function ensureMessarioAiGenerateBundle(
  token: string,
  panelOrigin: string,
  externalUserId: string,
  metadata: MessarioAiBundleEnabled,
  fetchValue: typeof fetch = fetch,
  storage: Storage = window.sessionStorage,
  forceRefresh = false
): Promise<MessarioAiGenerateBundleEnabled> {
  if(!forceRefresh) {
    const cached = getMessarioAiGenerateBundleCache(externalUserId, storage);
    if(cached && generateBundleMatchesMetadata(cached, metadata)) {
      return cached;
    }
  }

  const fetched = await fetchMessarioAiGenerateBundle(token, panelOrigin, fetchValue);
  if(!fetched.enabled) {
    clearMessarioAiGenerateBundleCache(externalUserId, storage);
    throw new MessarioAiFetchFailure(
      'configuration',
      'AI-менеджер выключен. Обновите страницу после настройки в Panel.'
    );
  }

  saveMessarioAiGenerateBundleCache(externalUserId, fetched, storage);
  return fetched;
}
